import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  content?: ReactNode;
  children: ReactElement;
  placement?: TooltipPlacement;
  delayMs?: number;
  disabled?: boolean;
  /** Hover only (nav/sidebar) or hover + keyboard focus (default). */
  trigger?: "hover" | "hover-focus";
  className?: string;
  contentClassName?: string;
}

const OFFSET = 8;
const VIEWPORT_PAD = 6;

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (value: T | null) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(value);
      else if (ref && typeof ref === "object") {
        (ref as { current: T | null }).current = value;
      }
    }
  };
}

function hasTooltipContent(content: ReactNode): boolean {
  if (content === undefined || content === null || content === false)
    return false;
  if (typeof content === "string") return content.trim().length > 0;
  return true;
}

function computeCoords(
  anchor: DOMRect,
  tip: DOMRect,
  placement: TooltipPlacement,
): { top: number; left: number } {
  let top = 0;
  let left = 0;

  switch (placement) {
    case "bottom":
      top = anchor.bottom + OFFSET;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
      break;
    case "left":
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.left - tip.width - OFFSET;
      break;
    case "right":
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.right + OFFSET;
      break;
    default:
      top = anchor.top - tip.height - OFFSET;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
  }

  left = Math.max(
    VIEWPORT_PAD,
    Math.min(left, window.innerWidth - tip.width - VIEWPORT_PAD),
  );
  top = Math.max(
    VIEWPORT_PAD,
    Math.min(top, window.innerHeight - tip.height - VIEWPORT_PAD),
  );
  return { top, left };
}

export function Tooltip({
  content,
  children,
  placement = "top",
  delayMs = 400,
  disabled,
  trigger = "hover-focus",
  className,
  contentClassName,
}: TooltipProps) {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  const inactive = disabled || !hasTooltipContent(content);

  const updateCoords = useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    setCoords(
      computeCoords(
        anchor.getBoundingClientRect(),
        tip.getBoundingClientRect(),
        placement,
      ),
    );
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
    const onScroll = () => updateCoords();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, content, updateCoords]);

  const clearShowTimer = () => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  };

  const show = () => {
    if (inactive) return;
    clearShowTimer();
    showTimer.current = setTimeout(() => setOpen(true), delayMs);
  };

  const hide = () => {
    clearShowTimer();
    setOpen(false);
    setCoords(null);
  };

  useEffect(() => () => clearShowTimer(), []);

  if (inactive || !isValidElement(children)) {
    return children;
  }

  type TooltipChildProps = {
    className?: string | ((...args: unknown[]) => string);
    onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
    onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
    onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
    onFocus?: (event: FocusEvent<HTMLElement>) => void;
    onBlur?: (event: FocusEvent<HTMLElement>) => void;
    "aria-describedby"?: string;
  };

  const childElement = children as ReactElement<TooltipChildProps & { ref?: Ref<HTMLElement> }>;
  const childRef = childElement.props.ref;
  const childClassName = childElement.props.className;
  const mergedClassName =
    typeof childClassName === "function"
      ? className
        ? (...args: unknown[]) =>
            cn(childClassName(...args), className)
        : childClassName
      : cn(childClassName, className);

  const child = cloneElement(
    childElement,
    {
      ref: mergeRefs(anchorRef, childRef),
      className: mergedClassName,
      onMouseEnter: (event: MouseEvent<HTMLElement>) => {
        childElement.props.onMouseEnter?.(event);
        show();
      },
      onMouseLeave: (event: MouseEvent<HTMLElement>) => {
        childElement.props.onMouseLeave?.(event);
        hide();
      },
      onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
        childElement.props.onPointerDown?.(event);
        hide();
      },
      onClick: (event: ReactMouseEvent<HTMLElement>) => {
        childElement.props.onClick?.(event);
        hide();
      },
      onFocus:
        trigger === "hover-focus"
          ? (event: FocusEvent<HTMLElement>) => {
              childElement.props.onFocus?.(event);
              show();
            }
          : childElement.props.onFocus,
      onBlur:
        trigger === "hover-focus"
          ? (event: FocusEvent<HTMLElement>) => {
              childElement.props.onBlur?.(event);
              hide();
            }
          : childElement.props.onBlur,
      "aria-describedby": open ? tooltipId : undefined,
    } as TooltipChildProps & { ref?: Ref<HTMLElement> },
  );

  return (
    <>
      {child}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={tooltipId}
            role="tooltip"
            className={cn(
              "pointer-events-none fixed z-[9999] max-w-xs rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg",
              "whitespace-pre-line dark:bg-slate-700",
              contentClassName,
            )}
            style={
              coords
                ? { top: coords.top, left: coords.left }
                : { top: -9999, left: -9999, visibility: "hidden" as const }
            }
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

interface TooltipTextProps {
  text: string;
  className?: string;
  as?: "span" | "p" | "div";
}

/** Truncated text with a tooltip when content overflows. */
export function TooltipText({
  text,
  className,
  as: Tag = "span",
}: TooltipTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const node = (
    <Tag ref={ref as never} className={cn("truncate", className)}>
      {text}
    </Tag>
  );

  if (!overflowing) return node;
  return <Tooltip content={text}>{node}</Tooltip>;
}
